import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import IcecreamRoundedIcon from "@mui/icons-material/IcecreamRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useNavigate } from "react-router-dom";
import Sidebar from "./AdminPanel/Sidebar";
import TopBar from "./AdminPanel/TopBar";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";
import { useAuth } from "../../context/AuthContext";

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("es-EC", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
}

function userCan(user, code) {
  if (!user) return false;
  if (user.role === "super_admin" || user.role === "admin") return true;
  return (user.permissions || []).includes(code);
}

function isFacturada(orden) {
  return orden?.facturada === true || orden?.facturada === "true" || orden?.facturada === 1 || orden?.facturada === "1";
}

export default function AdminOrden() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [ordenes, setOrdenes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const rowsPerPage = isMobile ? 10 : 30;
  const token = localStorage.getItem("accessToken");

  const canCreate = userCan(user, "ventas.create");

  const loadOrdenes = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (search.trim()) {
        params.append("q", search.trim());
      }

      const response = await fetch(`/api/ventas/ordenes/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar las órdenes");
        return;
      }

      setOrdenes(data.results || []);
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(0);
      loadOrdenes();
    }, 350);

    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const visibleOrdenes = useMemo(() => {
    const start = page * rowsPerPage;
    return ordenes.slice(start, start + rowsPerPage);
  }, [ordenes, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const smallButtonSx = {
    minHeight: 34,
    px: 1.6,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const invoiceButtonSx = {
    minHeight: 34,
    px: 1.6,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      opacity: 0.55,
      color: dashboardTheme.button_text_color,
      bgcolor: dashboardTheme.button_color,
    },
  };

  const goToInvoice = (orden) => {
    navigate(`/admin-panel/facturacion/nueva?orden=${orden.id}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 26, md: 30 },
                  fontWeight: 600,
                  color: dashboardTheme.primary_color,
                  lineHeight: 1.2,
                }}
              >
                Órdenes de trabajo
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<IcecreamRoundedIcon />}
                  onClick={() => navigate("/admin-panel/ordenes/sabores")}
                  sx={outlinedButtonSx}
                >
                  Sabores
                </Button>

                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => navigate("/admin-panel/ordenes/nueva")}
                    sx={dashboardTheme.buttonStyles.contained}
                  >
                    Nueva orden
                  </Button>
                )}
              </Box>
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={dashboardTheme.tableStyles.toolbar}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar por cliente o mesa"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    maxWidth: { xs: "100%", md: 420 },
                    ...dashboardTheme.inputStyles,
                  }}
                />
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Orden
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Cliente / Referencia
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Mesa
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Estado
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Total
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Fecha
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleOrdenes.map((orden) => {
                        const facturada = isFacturada(orden);

                        return (
                          <TableRow
                            key={orden.id}
                            hover
                            sx={{
                              ...dashboardTheme.tableStyles.bodyRow,
                              cursor: "pointer",
                            }}
                          >
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              #{orden.numero}
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: dashboardTheme.text_color,
                                  lineHeight: 1.25,
                                }}
                              >
                                {orden.cliente_referencia || "Consumidor final"}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {orden.mesa || "-"}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {orden.estado || "-"}
                            </TableCell>

                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              ${formatMoney(orden.total)}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {formatDate(orden.creado_en)}
                            </TableCell>

                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    navigate("/admin-panel/ordenes/nueva", {
                                      state: { orden },
                                    })
                                  }
                                  sx={smallButtonSx}
                                >
                                  Ver
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<ReceiptLongRoundedIcon />}
                                  disabled={facturada}
                                  onClick={() => goToInvoice(orden)}
                                  sx={invoiceButtonSx}
                                >
                                  {facturada ? "Facturada" : "Facturar"}
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {!loading && visibleOrdenes.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            No hay órdenes registradas
                          </TableCell>
                        </TableRow>
                      )}

                      {loading && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            Cargando órdenes...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={ordenes.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}